import cloneDeep from 'clone-deep'
import { InputType } from 'config'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { getBankerData, postReviewFormAppraiserAddLogs, updateBankerData } from 'services'
import { Button, Modal } from 'stories/components'
import { InputConvert, InputValidate, openAuditLog } from 'utils'
import { RenderInput } from 'utils/RenderInput'

const APPRAISER_ADD_LOG_PREFIX = 'appraiserAdd_'

interface Props {
  onClose: () => void
  reviewFormId: number
  loanNumber: number
  prefillData: {
    companyName?: string
    fullName?: string
    phone?: string
    email?: string
    address?: string
    licenseNumber?: string
    expireDate?: string
  }
}

export const AddAppraiserFromReviewModal = ({ onClose, prefillData, reviewFormId, loanNumber }: Props) => {
  const [data, setData] = useState<Record<string, any>>({})
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [inputs, setInputs] = useState<Record<string, InputType>>({})
  const [isLoading, setIsLoading] = useState(false)
  const [isFetching, setIsFetching] = useState(true)

  useEffect(() => {
    fetchInputs()
  }, [])

  const fetchInputs = async () => {
    setIsFetching(true)
    try {
      const res = await getBankerData({
        table: 'appraiser',
        query: '',
        pageNum: 0,
        orderBy: 'createdAt',
        orderDir: '-1',
        count: 1,
      })
      if (res.success && res.inputs) {
        setInputs(res.inputs)
        // Pre-fill data from ReviewForm
        const initialData: Record<string, any> = {}
        if (prefillData.companyName) initialData.companyName = prefillData.companyName
        if (prefillData.fullName) initialData.fullName = prefillData.fullName
        if (prefillData.phone) initialData.phone = prefillData.phone
        if (prefillData.email) initialData.email = prefillData.email
        if (prefillData.address) initialData.address = prefillData.address
        if (prefillData.licenseNumber) initialData.licenseNumber = prefillData.licenseNumber
        if (prefillData.expireDate) initialData.expireDate = prefillData.expireDate
        setData(initialData)
      }
    } catch (error) {
      console.error('Failed to fetch appraiser inputs:', error)
      toast('Failed to load appraiser form', { type: 'error' })
    }
    setIsFetching(false)
  }

  const onChange = (key: string, value: string) => {
    const temp = cloneDeep(data)
    const eTemp = cloneDeep(errors)
    temp[key] = InputConvert(inputs[key], value)
    eTemp[key] = ''
    setData(temp)
    setErrors(eTemp)
  }

  const onSubmit = async () => {
    const eTemp = cloneDeep(errors)
    let noError = true
    const json: Record<string, any> = {}

    Object.keys(inputs).forEach((key) => {
      json[key] = data[key]
      const error = InputValidate({ ...inputs[key], value: data[key] })
      eTemp[key] = error
      if (error.length) noError = false
    })

    setErrors(eTemp)
    if (!noError) return

    setIsLoading(true)
    try {
      const res = await updateBankerData({
        table: 'appraiser',
        data: json,
        id: 0, // 0 means new record
      })
      if (res.success) {
        try {
          await postReviewFormAppraiserAddLogs(reviewFormId, json)
        } catch {
          toast('Appraiser was added, but saving the audit log failed.', { type: 'warning' })
        }
        onClose()
      } else {
        toast('Failed to add appraiser', { type: 'error' })
      }
    } catch (error) {
      console.error('Failed to add appraiser:', error)
      toast('Failed to add appraiser', { type: 'error' })
    }
    setIsLoading(false)
  }

  const onShowHistory = (key: string) => {
    const input = inputs[key]
    const logField = `${APPRAISER_ADD_LOG_PREFIX}${key}`
    const options: Record<string, any> = {
      table: 'Valuation Review',
      field: logField,
      fieldLabel: input?.title || key,
      keys: { id: reviewFormId, field: logField, loanNumber },
    }
    if (input?.options) options.map = input.options
    openAuditLog(options)
  }

  return (
    <Modal title="Add Appraiser" isOpen onClose={onClose} titleOkay="" titleCancel="">
      <div className="w-196">
        {isFetching ? (
          <div className="flex justify-center py-8">
            <span className="text-gray-500">Loading form...</span>
          </div>
        ) : (
          <>
            <div className="grid gap-4 md:grid-cols-3 grid-cols-1">
              {Object.keys(inputs).map((key) => {
                const input = {
                  ...inputs[key],
                  history: true,
                  value: data[key] === undefined ? inputs[key].value : data[key],
                  error: errors[key],
                }
                if (input.visible === false) return null
                return (
                  <div className={`input md:col-span-${input.span || 1}`} key={key}>
                    <RenderInput input={input} Key={key} onChange={onChange} showHistory={onShowHistory} />
                  </div>
                )
              })}
            </div>
            <div className="flex justify-center gap-4 mt-6">
              <Button onClick={onSubmit} className="min-w-[120px] py-1" loading={isLoading}>
                Add
              </Button>
              <Button onClick={onClose} className="py-1" color="red">
                Cancel
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}
